import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RequirementsProps } from '../../tools/context';
import { useTranslation } from '../../tools/i18n';
import { Cross } from '../Picto/Cross';

export type NoticeLevel = 'info' | 'vigilance' | 'alerte';

export interface NoticeData {
  enabled?: boolean;
  level?: NoticeLevel;
  title?: string | Record<string, string>;
  message?: string | Record<string, string>;
  ctaLabel?: string | Record<string, string>;
  ctaUrl?: string;
  ctaOpenInNewTab?: boolean;
  startAt?: string;
  endAt?: string;
  dismissible?: boolean;
  /** @deprecated Ancien schéma HTML */
  desktopContent?: string;
  mobileContent?: string;
  background?: string;
  hideNotice?: boolean;
  hideBanner?: boolean;
}

export interface TopPageNoticeProps {
  notice?: NoticeData;
  requirements?: RequirementsProps;
}

const CLOSE_LABELS: Record<string, string> = {
  fr: 'Fermer',
  en: 'Close',
  de: 'Schließen',
};

const NOTICE_HEIGHT_VAR = '--top-page-notice-height';
const NOTICE_ACTIVE_CLASS = 'has-top-page-notice';

const resolveText = (
  value: string | Record<string, string> | undefined,
  language: string
): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const lang = (language || 'fr').slice(0, 2);
  return value[lang] || value.fr || value.en || Object.values(value).find(Boolean) || '';
};

const isWithinSchedule = (startAt?: string, endAt?: string): boolean => {
  const now = Date.now();
  if (startAt) {
    const start = Date.parse(startAt);
    if (!Number.isNaN(start) && now < start) return false;
  }
  if (endAt) {
    const end = Date.parse(endAt);
    if (!Number.isNaN(end) && now > end) return false;
  }
  return true;
};

const buildDismissKey = (title: string, endAt?: string): string =>
  `topPageNotice:${title}|${endAt || 'open'}`;

const clearNoticeLayoutOffset = () => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty(NOTICE_HEIGHT_VAR);
  document.documentElement.classList.remove(NOTICE_ACTIVE_CLASS);
};

const applyNoticeLayoutOffset = (height: number) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(NOTICE_HEIGHT_VAR, `${height}px`);
  document.documentElement.classList.add(NOTICE_ACTIVE_CLASS);
};

const LevelIcon = ({ level }: { level: NoticeLevel }) => {
  if (level === 'info') {
    return (
      <svg className="topPageNotice__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M12 11v5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="12" cy="8" r="1.25" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg className="topPageNotice__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 3.5L21 19H3L12 3.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M12 10v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
    </svg>
  );
};

export const TopPageNotice = (props: TopPageNoticeProps) => {
  const { notice } = props;
  const { language } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const rootRef = useRef<HTMLElement>(null);

  const title = resolveText(notice?.title, language);
  const message = resolveText(notice?.message, language);
  const ctaLabel = resolveText(notice?.ctaLabel, language);
  const level: NoticeLevel =
    notice?.level === 'info' || notice?.level === 'alerte' ? notice.level : 'vigilance';
  const dismissible = notice?.dismissible !== false;
  const dismissKey = useMemo(() => buildDismissKey(title, notice?.endAt), [title, notice?.endAt]);
  const closeLabel = CLOSE_LABELS[(language || 'fr').slice(0, 2)] || CLOSE_LABELS.fr;

  const legacyHidden = notice?.hideNotice === true || notice?.hideBanner === true;
  const hasLegacyHtml = !!(notice?.desktopContent || notice?.mobileContent);
  const hasStructured = !!title;

  const isVisibleStructured =
    !!notice &&
    !!notice.enabled &&
    !!title &&
    isWithinSchedule(notice.startAt, notice.endAt) &&
    !(dismissible && dismissed);

  const isVisibleLegacy = !!notice && hasLegacyHtml && !hasStructured && !legacyHidden;

  useEffect(() => {
    if (typeof window === 'undefined' || !dismissible) return;
    try {
      if (sessionStorage.getItem(dismissKey) === '1') {
        setDismissed(true);
      }
    } catch {
      // ignore storage errors
    }
  }, [dismissible, dismissKey]);

  useLayoutEffect(() => {
    if (!isVisibleStructured && !isVisibleLegacy) {
      clearNoticeLayoutOffset();
      return clearNoticeLayoutOffset;
    }

    const el = rootRef.current;
    if (!el) return clearNoticeLayoutOffset;

    const syncHeight = () => {
      applyNoticeLayoutOffset(el.offsetHeight);
    };

    syncHeight();

    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncHeight()) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncHeight);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncHeight);
      clearNoticeLayoutOffset();
    };
  }, [isVisibleStructured, isVisibleLegacy, title, message, ctaLabel, level]);

  const handleDismiss = () => {
    setDismissed(true);
    clearNoticeLayoutOffset();
    try {
      sessionStorage.setItem(dismissKey, '1');
    } catch {
      // ignore storage errors
    }
  };

  if (!notice) return null;

  if (hasLegacyHtml && !hasStructured) {
    if (legacyHidden) return null;
    return (
      <aside ref={rootRef} className="topPageNotice topPageNotice--legacy-wrap">
        {!!notice.desktopContent && (
          <div
            className="topPageNotice topPageNotice--legacy desktop"
            nonce="__nonce__"
            style={notice.background ? { backgroundColor: notice.background } : undefined}
            dangerouslySetInnerHTML={{ __html: notice.desktopContent }}
          />
        )}
        {!!notice.mobileContent && (
          <div
            className="topPageNotice topPageNotice--legacy mobile"
            nonce="__nonce__"
            style={notice.background ? { backgroundColor: notice.background } : undefined}
            dangerouslySetInnerHTML={{ __html: notice.mobileContent }}
          />
        )}
      </aside>
    );
  }

  if (!isVisibleStructured) return null;

  const showCta = !!(notice.ctaUrl && ctaLabel);
  const openInNewTab = notice.ctaOpenInNewTab === true;

  return (
    <aside
      ref={rootRef}
      className={`topPageNotice topPageNotice--${level}`}
      role="status"
      aria-live="polite"
    >
      <div className="topPageNotice__inner">
        <LevelIcon level={level} />
        <div className="topPageNotice__body">
          <p className="topPageNotice__title">{title}</p>
          {message ? <p className="topPageNotice__message">{message}</p> : null}
          {showCta ? (
            <a
              className="topPageNotice__cta"
              href={notice.ctaUrl}
              {...(openInNewTab
                ? {target: '_blank', rel: 'noopener noreferrer'}
                : {})}
            >
              {ctaLabel}
            </a>
          ) : null}
        </div>
        {dismissible ? (
          <button
            type="button"
            className="topPageNotice__close"
            onClick={handleDismiss}
            aria-label={closeLabel}
          >
            <Cross width="12" height="12" />
          </button>
        ) : null}
      </div>
    </aside>
  );
};

export default TopPageNotice;
